import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { productQueries } from "@surgia/api/src/queryStore/admin/product";

interface UseAssignProductProps {
  tenantId: string;
  onSuccess?: (data: any) => void;
  onError?: (error: any) => void;
}

interface AssignProductParams {
  productIds: string[];
  parentProductId: number;
}

export const useAssignProduct = ({
  tenantId,
  onSuccess,
  onError,
}: UseAssignProductProps) => {
  const queryClient = useQueryClient();

  const assignProductMutation = useMutation({
    ...productQueries.mutations.assignProductVariant(tenantId),
    onSuccess: (data) => {
      toast.success("Product assignment updated successfully!");

      // Invalidate relevant queries to refetch data
      queryClient.invalidateQueries({
        queryKey: ["admin", "product", "variations"],
      });
      queryClient.invalidateQueries({
        queryKey: ["admin", "tenant", "products"],
      });

      // Call custom onSuccess if provided
      if (onSuccess) {
        onSuccess(data);
      }
    },
    onError: (error) => {
      toast.error("Failed to update product assignment. Please try again.");
      console.error("Error updating product assignment:", error);

      // Call custom onError if provided
      if (onError) {
        onError(error);
      }
    },
  });

  const assignProduct = async (params: AssignProductParams) => {
    return assignProductMutation.mutateAsync(params);
  };

  const toggleProductAssignment = async (
    productId: string,
    parentProductId: number
  ) => {
    // The same mutation is used for both assign and unassign
    // The API handles the toggle logic based on the current state
    return assignProductMutation.mutateAsync({
      productIds: [productId],
      parentProductId,
    });
  };

  return {
    assignProduct,
    toggleProductAssignment,
    assignProductMutation,
    isAssigning: assignProductMutation.isPending,
    isError: assignProductMutation.isError,
    error: assignProductMutation.error,
  };
};
